'use client'

/**
 * What a project write accepts, and the two hooks that perform one.
 *
 * **No `useMutation`, no query key, no `invalidateQueries`** — ADR 0012 rule 6, and #315's
 * acceptance criterion. The old `hooks/mutations/useProjectMutations.ts` wrapped each write in a
 * `useMutation` and followed it with two invalidations; all six were inert, because neither
 * `projectsQueryKey` nor `feedQueryKey` was registered against any query. The tree holds exactly
 * one `useQuery`, in `legacy/supabase-store/use-people.ts`.
 *
 * **The mechanics are already `document/`'s.** #315 records these three as raw `changeDoc` calls
 * and rule 2 breaches; #289 had already moved them, so each of these is a caller of
 * `document/operations/projects.ts` rather than a writer. `rg -in changedoc src/projects/` returns
 * nothing.
 *
 * **`isPending` is kept and is never observably true.** `project-create-form.tsx` disables its
 * submit button on it and swaps the label to "Creating…". The write is synchronous against a local
 * document, so the flag flips within one tick and no frame renders between — but removing the
 * member would be a signature change to a surface this step is only supposed to move, so it stays
 * and says so here.
 */

import { useCallback, useState } from 'react'
import { useDocumentOps, useDocumentView } from '@/document'
import type { ProjectStatus } from '@/document'

/** What the create form collects. `status` defaults to active. */
export interface ProjectDraft {
  name: string
  description?: string
  status?: ProjectStatus
}

/** What the sidebar edits: a status, a description, or both. */
export interface ProjectPatch {
  name?: string
  description?: string
  status?: ProjectStatus
}

export function useCreateProject() {
  const doc = useDocumentView()
  const ops = useDocumentOps()
  const [isPending, setIsPending] = useState(false)

  const mutate = useCallback(
    (
      draft: ProjectDraft,
      options?: { onSuccess?: (projectId: string) => void },
    ) => {
      if (!doc) throw new Error('Document not ready')

      setIsPending(true)
      try {
        const id = ops.createProject({
          name: draft.name,
          description: draft.description || '',
          status: draft.status ?? 'active',
        })
        // The id, not `doc.projects[id]`: `doc` is the view captured this render and
        // `createProject` mints the project into the *next* one, so the lookup is always
        // `undefined`. The old hook passed the same undefined under a `Project` annotation.
        options?.onSuccess?.(id)
      } finally {
        setIsPending(false)
      }
    },
    [doc, ops],
  )

  return { mutate, isPending }
}

export function useUpdateProject() {
  const doc = useDocumentView()
  const ops = useDocumentOps()

  const mutate = useCallback(
    ({ id, data }: { id: string; data: ProjectPatch }) => {
      if (!doc) throw new Error('Document not ready')

      // `updateProject` returns false when there is no such project — archived on another device
      // while the sidebar is open, say. Dropping the return value drops the write silently and
      // leaves the sidebar rendering as though it saved.
      if (!ops.updateProject(id, data)) {
        throw new Error(`Project ${id} not found`)
      }
    },
    [doc, ops],
  )

  return { mutate }
}
